import {
  Favorite as FavoriteUI,
  selectAllHotelsList,
  useToast,
  useWishlistMutation,
} from '@travel-frontend/ui-components';
import {useState, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Slide, toast} from 'react-toastify';

import {useAppSelector} from '@/redux/config';

type Props = {
  hotelId: string;
  onAddSuccess?: () => void;
};

export const Favorite = ({hotelId, onAddSuccess}: Props) => {
  const favoriteToast = useToast({
    transition: Slide,
    position: 'bottom-center',
    style: {
      maxWidth: '380px',
      minHeight: '42px',
      margin: '0 auto',
    },
  });
  const {t} = useTranslation('wishlist');
  const {addToWishlist, removeFromWishlist} = useWishlistMutation();
  const allHotelIds = useAppSelector(selectAllHotelsList);
  const isSelected = useMemo(
    () => allHotelIds.includes(hotelId),
    [allHotelIds, hotelId]
  );

  const [active, setActive] = useState(isSelected);

  const addHotel = useCallback(() => {
    toast.dismiss();
    setActive(true);
    addToWishlist(hotelId, {
      onSuccess: () => {
        favoriteToast({
          caption: t('toast.addHotel.success'),
          variant: 'success',
        });
        onAddSuccess?.();
      },
      onError: () => {
        setActive(false);
        favoriteToast({
          caption: t('toast.addHotel.error'),
          variant: 'error',
        });
      },
    });
  }, [addToWishlist, hotelId, favoriteToast, t, onAddSuccess]);

  const removeHotel = useCallback(() => {
    toast.dismiss();
    setActive(false);
    removeFromWishlist(hotelId, {
      onSuccess: () => {
        favoriteToast({
          caption: t('toast.removeHotel.success'),
          variant: 'info',
        });
      },
      onError: () => {
        setActive(true);
        favoriteToast({
          caption: t('toast.removeHotel.error'),
          variant: 'error',
        });
      },
    });
  }, [removeFromWishlist, hotelId, favoriteToast, t]);

  const handleFavorite = () => {
    return active ? removeHotel() : addHotel();
  };

  return (
    <FavoriteUI
      onClick={handleFavorite}
      variant="inherit"
      filled={active}
      color="interactionSecondary"
      as="button"
    />
  );
};
